"use client";
import { useCallback, useRef, useState } from "react";
import { signUp, signIn, fetchAuthSession } from "@aws-amplify/auth";
import { setDocumentCookie, getCookie, setPgsIdAuthCookie } from "@packages/lib/utlils/cookie-utils";
import {
  getProjectSpecificData,
  getCommonAPIHeaders,
  isPGS,
} from "@packages/lib/utlils/commonFunction";
import API_URL from "@packages/lib/server-actions/api-urls";
import { jwtDecode } from "jwt-decode";
import { v4 as uuidv4 } from "uuid";
import {
  LeadFormValues,
  LeadFormErrors,
  LeadFormPageType,
  LeadFormSubmissionContext,
  LeadFormInteractionPayload,
  LeadFormCmsItem,
} from "../types";
import { hasField, getInteractionType } from "../field-registry";
import {
  containsProfanity,
  PROFANITY_ERROR_MESSAGE,
} from "../utils/profanity-filter";
import { logClickstreamEvent } from "@packages/lib/utlils/clickstream";
import {
  CS_EVENT_TYPES,
  CS_SIGNUP_METHODS,
  CS_SIGNIN_METHODS,
  CS_FUNCTIONALITY,
  CS_ACTION_TYPE,
  CS_NETWORK_ID,
  CS_PGS_NETWORK_ID,
  CS_SYSTEM_NAME,
  CS_CTA_TITLES,
  getLeadFormPageName,
  getLeadFormRegistrationPageName,
  getLeadFormSubmissionPageName,
  getLeadFormSubmissionEventType,
  getLeadFormSubmissionCTATitle,
} from "@packages/lib/utlils/clickstream-constants";
import { triggerSigninToast } from "@packages/shared-components/common-utilities/toast/signin-toast";
import emitter, { CustomEvents } from "@packages/lib/eventEmitter/eventEmitter";

const API_DOMAIN = process.env.NEXT_PUBLIC_BFF_API_DOMAIN || "";

/** Check if pageType is a prospectus form (DLG-10182: partial registration flow) */
const isProspectusPageType = (pageType: string): boolean =>
  pageType === "digitalprospectus" || pageType === "physicalprospectus";

const getAuthHeaders = (token?: string) => ({
  ...getCommonAPIHeaders(),
  siteCode: process.env.SITE_CODE || "WU",
  Lp:"Y",
  "x-correlation-id": uuidv4(),
  ...(token
    ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` }
    : {}),
});

const isStrongPassword = (value: string) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(value);

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const isValidUKPostcode = (value: string) =>
  /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value.trim());

const ENQUIRY_MAX_LENGTH = 4000;
const isExistingUserAction = (action: string | undefined): boolean =>
  action === "PROCEED_TO_SIGN_IN" ||
  action === "FB_USER_PROCEED_WITH_NATIVE_SIGN_IN" ||
  action === "FB_USER_RESET_PASSWORD";

export const useLeadFormSubmit = (
  pageType: LeadFormPageType,
  submissionContext: LeadFormSubmissionContext,
  cmsItems: LeadFormCmsItem[],
  userDetails?: any,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // Background email check result
  const emailActionRef = useRef<string | null>(null);
  const emailMessageRef = useRef<string>("");
  const emailCheckingRef = useRef<string>("");

  // Exposed state: whether email already exists (for request info forms to block signin)
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);

  const redirectToForgotPassword = (email: string) => {
    if (typeof window === "undefined") return;
    const normalizedEmail = email.trim().toLowerCase();
    sessionStorage.setItem("reg-flow-email", normalizedEmail);
    sessionStorage.setItem("reg-flow-step", "signin");
    window.location.assign(isPGS() ? "/pgs/forgotpassword" : "/degrees/forgotpassword");
  };

  /** Check if email exists — called on email blur for background pre-check.
   *  For request info forms: sets emailAlreadyExists=true if user is registered,
   *  blocking submission and showing an inline error. */
  const checkEmailExists = useCallback(
    async (email: string): Promise<{ exists: boolean; action: string | null }> => {
      if (!email.trim() || !isValidEmail(email.trim())) return { exists: false, action: null };
      const normalizedEmail = email.trim().toLowerCase();
      if (emailCheckingRef.current === normalizedEmail && emailActionRef.current !== null) {
        // Already checked — return cached result
        const exists = isExistingUserAction(emailActionRef.current || undefined) || emailActionRef.current === "SOCIAL_ONLY_USER";
        return { exists, action: emailActionRef.current };
      }
      emailCheckingRef.current = normalizedEmail;
      try {
        const affiliateId = String(getProjectSpecificData("affiliateId"));
        const response = await fetch(
          `${API_DOMAIN}${API_URL.checkUserExistsHeW}`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ email: normalizedEmail, affiliateId }),
          },
        );
        if (response.ok) {
          const data = await response.json();
          emailActionRef.current = data?.action || null;
          emailMessageRef.current = data?.message || "";

          // Determine if user already exists — block signin for request info forms
          const exists = isExistingUserAction(data?.action || undefined) || data?.action === "SOCIAL_ONLY_USER";
          setEmailAlreadyExists(exists);
          return { exists, action: data?.action || null };
        }
      } catch {
        emailActionRef.current = null;
        emailMessageRef.current = "";
      }
      setEmailAlreadyExists(false);
      return { exists: false, action: null };
    },
    [],
  );

  /** Call partial registration API (DLG-10182) — used at step 1 for physical prospectus.
   *  Non-blocking: failure doesn't prevent the user from advancing to step 2. */
  const callPartialRegistration = useCallback(
    async (values: LeadFormValues): Promise<{ success: boolean }> => {
      const normalizedEmail = values.email.trim().toLowerCase();
      setIsRegistering(true);
      try {
        await fetch(`${API_DOMAIN}${API_URL.partialRegistration}`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            userDetails: {
              personalDetails: {
                email: normalizedEmail,
                firstName: values.firstName.trim(),
                secondName: values.lastName.trim(),
              },
              cognitoId: null,
            },
            userSourceType: "REQUEST_PROSPECTUS",
            marketingEmailFlags: values.stayInKnow ? "Y" : "N",
            studyLevel: values.studyLevel,
            affiliateId: String(getProjectSpecificData("affiliateId")),
          }),
        });
        return { success: true };
      } catch {
        // Registration API failure is non-blocking — continue to next step
        console.log("[useLeadFormSubmit] Register API call failed (non-blocking)");
        return { success: true };
      } finally {
        setIsRegistering(false);
      }
    },
    [],
  );

  /** Validate form values based on which fields exist in CMS */
  const validateForm = useCallback(
    (values: LeadFormValues, isAuthenticated?: boolean): LeadFormErrors => {
      const errors: LeadFormErrors = {};

      // Enquiry: required + profanity + 4000 char limit
      if (hasField(cmsItems, "enquiry")) {
        const enquiryText = values.enquiry.trim();
        if (!enquiryText) {
          errors.enquiry = "Please enter your enquiry";
        } else if (enquiryText.length > ENQUIRY_MAX_LENGTH) {
          errors.enquiry = `Maximum ${ENQUIRY_MAX_LENGTH} characters allowed`;
        } else if (containsProfanity(enquiryText)) {
          errors.enquiry = PROFANITY_ERROR_MESSAGE;
        }
      }

      // First name: required, min 2, alpha only
      if (!isAuthenticated && hasField(cmsItems, "firstName")) {
        if (!values.firstName.trim())
          errors.firstName = "Please enter your first name";
        else if (values.firstName.trim().length < 2)
          errors.firstName = "First name must be at least 2 characters";
      }

      // Last name: required, min 2, alpha only
      if (!isAuthenticated && hasField(cmsItems, "lastName")) {
        if (!values.lastName.trim())
          errors.lastName = "Please enter your last name";
        else if (values.lastName.trim().length < 2)
          errors.lastName = "Last name must be at least 2 characters";
      }

      // Email: required, valid format
      if (!isAuthenticated && hasField(cmsItems, "email")) {
        if (!values.email.trim())
          errors.email = "Please enter a valid email address";
        else if (!isValidEmail(values.email.trim()))
          errors.email = "Please enter a valid email address";
      }

      // Password: required, strong
      if (!isAuthenticated && hasField(cmsItems, "password")) {
        if (!values.password)
          errors.password = "Please enter your password";
        else if (!isStrongPassword(values.password))
          errors.password =
            "Use 8+ chars with uppercase, lowercase, number and special character";
      }

      // Study level: required
      if (hasField(cmsItems, "studyLevel") && !values.studyLevel) {
        errors.studyLevel = "Please select your study level";
      }

      // Year of entry: required
      if (hasField(cmsItems, "yearOfEntry") && !values.yearOfEntry) {
        errors.yearOfEntry = "Please select your year of entry";
      }

      // Postcode (request info): optional — only validate format if entered
      if (hasField(cmsItems, "postcode")) {
        if (values.postcode.trim() && !isValidUKPostcode(values.postcode)) {
          errors.postcode = "Please enter a valid UK postcode";
        }
      }

      // Country of residence: required
      if (hasField(cmsItems, "countryOfResidence") && !values.countryOfResidence) {
        errors.countryOfResidence = "Please select your country of residence";
      }

      // Address block validation (when addressBlock exists in CMS)
      if (hasField(cmsItems, "addressBlock")) {
        const isUK = values.countryOfResidence === "United Kingdom";
        const isROW = values.countryOfResidence === "Rest of the world";

        if (isUK || isROW) {
          if (isROW && !values.countrySelector) {
            errors.addressBlock = "Please select your country";
          } else if (
            !values.addressLine1.trim() ||
            !values.addressLine2.trim() ||
            !values.townCity.trim() ||
            !values.addressPostcode.trim()
          ) {
            errors.addressBlock = "Please enter the address fields";
          }
        }
      }
      return errors;
    },
    [cmsItems],
  );

  /** Build the interaction payload matching the new API structure */
  const buildPayload = useCallback(
    (values: LeadFormValues, checkedConsentIds?: Set<string>): LeadFormInteractionPayload => {
      const affiliateId = Number(getProjectSpecificData("affiliateId")) || null;
      const widgetId = Number(getCookie("widgetId")) || null;

      // Resolve country: use countrySelector (ROW dropdown) if available, otherwise countryOfResidence
      const resolvedCountry = values.countryOfResidence === "Rest of the world" && values.countrySelector ;

      return {
        interactionType: getInteractionType(pageType),
        interactionId: null,
        collegeId: Number(submissionContext.collegeId) || 0,
        courseId: submissionContext.courseId ? Number(submissionContext.courseId) : null,
        interactivityId: null,
        dateSent: null,
        affiliateId,
        emailContent: values.enquiry.trim() || null,
        providerEmailId: null,
        subOrderItemId: submissionContext.subOrderItemId ? Number(submissionContext.subOrderItemId) : null,
        profileId: null,
        widgetId,
        postEnquiryFlag: null,
        mobileFlag: null,
        appFlag: null,
        clientConsentFlag: (values.stayInKnow || (checkedConsentIds && checkedConsentIds.size > 0)) ? "Y" : null,
        clientConsentDate: submissionContext.clientConsentDate || null,
        orderItemId: submissionContext.orderItemId ?? null,
        ipAddress: null,
        odEventId: values.openDayEventId ? Number(values.openDayEventId) : null,
        qualificationInterest: values.studyLevel || null,
        subjectInterest: values.yearOfEntry || null,
        enquiryTypeId: null,
        trackUserActionId: null,
        createdAt: null,
        updatedAt: null,
        city: values.townCity.trim() || null,
        town: values.townCity.trim() || null,
        postCode: values.postcode.trim() || values.addressPostcode.trim() || null,
        addressLineOne: values.addressLine1.trim() || null,
        addressLineTwo: values.addressLine2.trim() || null,
        country: resolvedCountry || null,
        requestUrl: submissionContext.requestUrl || null,
        referrerUrl: submissionContext.referrerUrl || null,
        firstName: values.firstName.trim() || userDetails?.foreName || null,
        lastName: values.lastName.trim() || userDetails?.surname || null,
        userEmail: values.email.trim().toLowerCase() || userDetails?.email?.toLowerCase() || null,
        inTakeYear: values.yearOfEntry || userDetails?.yearOfEntry || null,
        marketingEmailFlags: (values.stayInKnow || (checkedConsentIds && checkedConsentIds.size > 0)) ? "Y" : "N",
        ...(isProspectusPageType(pageType) && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("nonAdvertiserFlag") === "Y"
          ? { nonAdvertiserFlag: "Y" }
          : {}),
        ...(isPGS() ? { institutionName: submissionContext.institutionNameDisplay ?? null } : {}),
      };
    },
    [pageType, submissionContext, cmsItems, userDetails],
  );

  /**
   * Full submit flow:
   * 1. Validate all fields (including profanity)
   * 2. Check email exists (use cached result from background check)
   * 3. If new user → signUp then signIn / If existing → signIn only
   * 4. Get JWT token
   * 5. POST interaction to BFF
   */
  const handleSubmit = useCallback(
    async (
      values: LeadFormValues,
      isAuthenticated?: boolean,
      checkedConsentIds?: Set<string>,
    ): Promise<{ success: boolean; errors?: LeadFormErrors }> => {
      const errors = validateForm(values, isAuthenticated);
      if (Object.keys(errors).length > 0) return { success: false, errors };

      if (isSubmitting) return { success: false };
      setIsSubmitting(true);
      setFormError("");

      const normalizedEmail = values.email.trim().toLowerCase();

      try {
        let idToken: string | undefined;

        if (isAuthenticated) {
          // Already signed in — just get the existing session token
          const session = await fetchAuthSession({ forceRefresh: true });
          idToken = session?.tokens?.idToken?.toString();
        } else if (isProspectusPageType(pageType)) {
          // DLG-10182: Prospectus partial registration flow — NO Cognito signUp/signIn
          // Physical: registration already called at step 1 (via callPartialRegistration)
          // Digital: single-step, so call registration here
          if (pageType === "digitalprospectus") {
            try {
              await fetch(`${API_DOMAIN}${API_URL.partialRegistration}`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                  userDetails: {
                    personalDetails: {
                      email: normalizedEmail,
                      firstName: values.firstName.trim(),
                      secondName: values.lastName.trim(),
                    },
                    cognitoId: null,
                  },
                  userSourceType: "REQUEST_PROSPECTUS",
                  marketingEmailFlags: values.stayInKnow ? "Y" : "N",
                  studyLevel: values.studyLevel,
                  affiliateId: String(getProjectSpecificData("affiliateId")),
                }),
              });
            } catch {
              // Registration API failure is non-blocking — continue to submit interaction
              console.log("[useLeadFormSubmit] Register API call failed (non-blocking)");
            }
          }
          // Physical prospectus: registration already done at step 1, skip here

          // Submit interaction WITHOUT idToken — pass email in payload (prospectus guest API)
          const payload = buildPayload(values, checkedConsentIds);
          const response = await fetch(`${API_DOMAIN}${API_URL.leadProfileInteractionsProspectusSubmit}`, {
            method: "POST",
            headers: getAuthHeaders(), // No Authorization token
            body: JSON.stringify({ ...payload, userEmail: normalizedEmail }),
          });

          const responseData = await response.json().catch(() => null);

          if (!response.ok || responseData?.status === 409) {
            if (response.status === 409 || responseData?.status === 409) {
              const siteName = isPGS() ? "Postgraduate Search" : "Whatuni";
              triggerSigninToast(`You've exceeded the number of times you can contact this provider through ${siteName}.`);
              emitter.emit(CustomEvents.PostAuthRefresh);
              return { success: false };
            }
            throw new Error(`Interaction submission failed: ${response.status}`);
          }

          // Clickstream: Java parity — fire registration clickstream for prospectus partial reg
          // Java: GetProspectusFormController.registrationClickStream() fires UserRegistered after DB insert
          logClickstreamEvent({
            eventType: CS_EVENT_TYPES.USER_REGISTERED,
            pageName: localStorage?.getItem("previousPageName") || getLeadFormRegistrationPageName(pageType),
            actionType: CS_ACTION_TYPE,
            functionalityName: CS_FUNCTIONALITY.SIGN_UP,
            signupMethod: CS_SIGNUP_METHODS.SIGN_UP,
            CTATitle: CS_CTA_TITLES.CONTINUE,
            interestedIntakeYear: values.yearOfEntry || "",
            networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
          });

          // Clickstream: Java parity — fire form submission clickstream
          // Java: GetProspectusFormController.clickStreamCall() fires ProspectusRequested/DigitalProspectusRequested
          logClickstreamEvent({
            eventType: getLeadFormSubmissionEventType(pageType),
            pageName: getLeadFormSubmissionPageName(pageType),
            actionType: CS_ACTION_TYPE,
            CTATitle: getLeadFormSubmissionCTATitle(pageType),
            isAdvertiser: submissionContext.subOrderItemId ? "Y" : "N",
            idpcInstitutionId: submissionContext.collegeId || "",
            subOrderItemId: submissionContext.subOrderItemId || "",
            sourceOrderItemIdForEnq: submissionContext.sponsoredOrderItemId || "",
            networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
            idpcCourseId: submissionContext.courseId || "",
            systemname: CS_SYSTEM_NAME,
            surveyConsent: values.stayInKnow ? "Y" : "N",
            newsletterConsent: values.stayInKnow ? "Y" : "N",
            solusConsent: values.stayInKnow ? "Y" : "N",
            interestedIntakeYear: values.yearOfEntry || "",
            ...(pageType === "digitalprospectus" ? {
              functionalityName: CS_FUNCTIONALITY.DOWNLOAD_PROSPECTUS,
              downloadFlag: "Yes",
            } : {}),
          });

          setIsSuccess(true);
          return { success: true };
        } else {
          // Determine if user exists (from background check or fresh check)
          let action = emailActionRef.current;
          if (action === null) {
            // Background check didn't complete — do it now
            try {
              const affiliateId = String(getProjectSpecificData("affiliateId"));
              const checkResponse = await fetch(
                `${API_DOMAIN}${API_URL.checkUserExistsHeW}`,
                {
                  method: "POST",
                  headers: getAuthHeaders(),
                  body: JSON.stringify({ email: normalizedEmail, affiliateId }),
                },
              );
              if (checkResponse.ok) {
                const checkData = await checkResponse.json();
                action = checkData?.action || null;
                emailActionRef.current = action;
                emailMessageRef.current = checkData?.message || "";
              }
            } catch {
              action = null; // Assume new user on error
            }
          }

          if (action === "SOCIAL_ONLY_USER") {
            setFormError(
              `This email is already registered. Please sign in via ${emailMessageRef.current || "Google or Apple"}`
            );
            return { success: false };
          }

          if (action === "FB_USER_RESET_PASSWORD") {
            redirectToForgotPassword(normalizedEmail);
            return { success: false };
          }

          const userExists = isExistingUserAction(action || undefined);

          // Block existing users from submitting request info — only registration allowed
          if (userExists) {
            setEmailAlreadyExists(true);
            setFormError("Email address already exists, Please Sign in");
            return { success: false };
          }

          if (!userExists) {
            // New user → signUp first
            await signUp({
              username: normalizedEmail,
              password: values.password,
              options: {
                userAttributes: {
                  "custom:user_source": "SIGN_UP",
                  given_name: values.firstName.trim(),
                  family_name: values.lastName.trim(),
                  website: String(getProjectSpecificData("affiliateId")),
                },
                clientMetadata: {
                  marketingEmailFlags: (values.stayInKnow || (checkedConsentIds && checkedConsentIds.size > 0)) ? "Y" : "N",
                  site: getProjectSpecificData("siteCode"),
                  referralLeadProfile: "Y",
                  leadFormRegistration: "Y",
                  studyLevel: values.studyLevel,
                  intakeYear: values.yearOfEntry || "",
                },
              },
            });
          }

          // signIn (new user only — existing users are blocked above)
          const signInResponse = await signIn({
            username: normalizedEmail,
            password: values.password,
            options: {
              authFlowType: "USER_PASSWORD_AUTH",
              clientMetadata: {
                referralLeadProfile: "Y",
              },
            },
          });

          if (signInResponse?.nextStep?.signInStep !== "DONE") {
            setFormError("Unable to complete sign in. Please try again.");
            return { success: false };
          }

          // Get JWT token
          const session = await fetchAuthSession({ forceRefresh: true });
          idToken = session?.tokens?.idToken?.toString();
        }

        if (!idToken) {
          setFormError("Unable to complete authentication. Please try again.");
          return { success: false };
        }

        
        if (isPGS()) {
          setPgsIdAuthCookie(idToken);
        } else {
          setDocumentCookie({ name: "wuIdToken", value: idToken });
        }

        // Clickstream: DLG-9950 — fire UserRegistered (new user) or SignedIn (existing user) in lead form flow
        // Java parity: QLController.registrationClickStream() / GetProspectusFormController.registrationClickStream()
        if (!isAuthenticated) {
          const userExists = isExistingUserAction(emailActionRef.current || undefined);
          const csRegPageName = localStorage?.getItem("previousPageName") || getLeadFormRegistrationPageName(pageType);
          if (!userExists) {
            // New user registered via lead form
            // Java: eventType=UserRegistered, pageName="Enquiry page", signupMethod="Sign Up", CTATitle="REQUEST INFO"/"GET PROSPECTUS"
            logClickstreamEvent({
              eventType: CS_EVENT_TYPES.USER_REGISTERED,
              pageName: csRegPageName,
              CTATitle: pageType === "requestinfo" ? "REQUEST INFO" : "GET PROSPECTUS",
              functionalityName: CS_FUNCTIONALITY.SIGN_UP,
              actionType: CS_ACTION_TYPE,
              signupMethod: CS_SIGNUP_METHODS.SIGN_UP,
              networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
              surveyConsent: values.stayInKnow ? "Y" : "N",
              newsletterConsent: values.stayInKnow ? "Y" : "N",
              solusConsent: values.stayInKnow ? "Y" : "N",
              interestedIntakeYear: values.yearOfEntry || "",
            });
          } else {
            // Existing user signed in via lead form
            // Java: eventType=SignedIn, pageName="Enquiry page", signedInMethod="Sign In"
            logClickstreamEvent({
              eventType: CS_EVENT_TYPES.SIGNED_IN,
              pageName: csRegPageName,
              CTATitle: CS_CTA_TITLES.SIGN_IN,
              functionalityName: CS_FUNCTIONALITY.SIGN_IN,
              actionType: CS_ACTION_TYPE,
              signedinMethod: CS_SIGNIN_METHODS.SIGN_IN,
              networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
            });
          }
        }

        // POST interaction to BFF
        const payload = buildPayload(values, checkedConsentIds);
        // For signed-in users, ensure user fields are populated from decoded JWT if not already set
        if (isAuthenticated && idToken) {
          const decoded: any = jwtDecode(idToken);
          if (!payload.userEmail && decoded?.email) {
            payload.userEmail = decoded.email.toLowerCase();
          }
          if (!payload.firstName && decoded?.given_name) {
            payload.firstName = decoded.given_name;
          }
          if (!payload.lastName && decoded?.family_name) {
            payload.lastName = decoded.family_name;
          }
        }
        const response = await fetch(`${API_DOMAIN}${API_URL.leadProfileInteractionsSubmit}`, {
          method: "POST",
          headers: getAuthHeaders(idToken),
          body: JSON.stringify(payload),
        });

        const responseData = await response.json().catch(() => null);

        if (!response.ok || responseData?.status === 409) {
          if (response.status === 409 || responseData?.status === 409) {
            const siteName = isPGS() ? "PGS" : "Whatuni";
            triggerSigninToast(`You've exceeded the number of times you can contact this provider through ${siteName}.`);
            emitter.emit(CustomEvents.PostAuthRefresh);
            return { success: false };
          }
          throw new Error(
            `Interaction submission failed: ${response.status}`,
          );
        }

        // Clickstream: Java parity — fire form submission clickstream after successful interaction save
        // Java: QLController.clickStreamCall() / GetProspectusFormController.clickStreamCall()
        // Fires: EnquiryFormSubmitted (RI) / ProspectusRequested (GP) / DigitalProspectusRequested (DP)
        logClickstreamEvent({
          eventType: getLeadFormSubmissionEventType(pageType),
          pageName: getLeadFormSubmissionPageName(pageType),
          actionType: CS_ACTION_TYPE,
          CTATitle: getLeadFormSubmissionCTATitle(pageType),
          isAdvertiser: submissionContext.subOrderItemId ? "Y" : "N",
          idpcInstitutionId: submissionContext.collegeId || "",
          subOrderItemId: submissionContext.subOrderItemId || "",
          sourceOrderItemIdForEnq: submissionContext.sponsoredOrderItemId || "",
          networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
          idpcCourseId: submissionContext.courseId || "",
          systemname: CS_SYSTEM_NAME,
          surveyConsent: values.stayInKnow ? "Y" : "N",
          newsletterConsent: values.stayInKnow ? "Y" : "N",
          solusConsent: values.stayInKnow ? "Y" : "N",
          interestedIntakeYear: values.yearOfEntry || "",
          ...(pageType === "digitalprospectus" ? {
            functionalityName: CS_FUNCTIONALITY.DOWNLOAD_PROSPECTUS,
            downloadFlag: "Yes",
          } : {}),
        });

        setIsSuccess(true);
        return { success: true };
      } catch (error: any) {
        const errorName = error?.name || "";
        if (errorName === "UsernameExistsException") {
          setFormError(
            "This email is already registered. Please sign in instead.",
          );
        } else if (errorName === "NotAuthorizedException") {
          setFormError("Incorrect password. Please try again.");
        } else if (errorName === "LimitExceededException") {
          setFormError(
            "Your account is locked! Kindly reset your password.",
          );
        } else {
          setFormError("Unable to submit. Please try again.");
        }
        // Clickstream: Log signupFailureReason for Cognito service errors (400, 403, 500, network issues)
        const httpStatusCode = error?.$metadata?.httpStatusCode || "";
        const signupFailureReason = httpStatusCode
          ? `${httpStatusCode} - ${error?.message || errorName}`
          : error?.message || errorName || "Sign up failed";
        logClickstreamEvent({
          eventType: CS_EVENT_TYPES.USER_REGISTERED,
          pageName: localStorage?.getItem("previousPageName") || getLeadFormRegistrationPageName(pageType),
          CTATitle: pageType === "requestinfo" ? "REQUEST INFO" : "GET PROSPECTUS",
          functionalityName: CS_FUNCTIONALITY.SIGN_UP,
          actionType: CS_ACTION_TYPE,
          signupMethod: CS_SIGNUP_METHODS.SIGN_UP,
          networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
          signupFailureReason,
        });
        return { success: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [validateForm, isSubmitting, buildPayload],
  );

  return {
    isSubmitting,
    isRegistering,
    formError,
    isSuccess,
    validateForm,
    handleSubmit,
    callPartialRegistration,
    checkEmailExists,
    setFormError,
    emailAlreadyExists,
  };
};
